<template>
    <div>
        <div class="text-left mx-8 mt-8 text-sm">
            <p class="mb-2">
                LSHTM values the diversity of its community and is committed to the creation of a positive environment which is fair, welcoming and inclusive and where everyone is treated with dignity and respect. We welcome applicants with varied experiences and different backgrounds, and are committed to ensure that no student with potential is deterred from applying.
            </p>
            <p class="mb-2">
                Information provided is used solely for the purpose of monitoring application and admission rates and forms no part of the selection procedure.  All information is confidential and will not be disclosed to the programme selection team.
            </p>
        </div>
        <div class="w-full text-left pl-8 pt-4 -mb-4">* All fields required</div>
        <div class="mb-8">
            <form @submit.prevent="update">
                <div class="p-8 pb-2 flex flex-wrap text-left" @change="dirtyForm()" @keydown="dirtyForm">
                    <h1 class="text-green-600 text-lg">* How did you hear about us?</h1>
                    <div class="mb-2 w-full border-b-2" />
                    <label class="w-full">How did you hear about the School and the course to which you have applied?</label>
                    <div class="w-full text-sm mb-2">(If you do not wish to provide this information then please select 'Information Refused')</div>
                    <select-input v-model="form.sources_of_interest"
                                  class="pr-6"
                                  :error="form.errors?.sources_of_interest"
                                  @update:modelValue="value = $event"
                    >
                        <option v-for="item in sourcesOfInterest" :key="item.reference" :value="item.code">{{ item.name }}</option>
                    </select-input>

                    <h1 class="text-green-600 text-lg w-full">* Ethnic Origin</h1>
                    <div class="mb-2 w-full border-b-2" />
                    <label class="w-full">To which ethnic group do you consider you belong?</label>
                    <div class="w-full text-sm mb-2">(If you do not wish to provide this information then please select 'Information Refused')</div>
                    <select-input v-model="form.ethnic_origin"
                                  class="pr-6"
                                  :error="form.errors?.ethnic_origin"
                                  @update:modelValue="value = $event"
                    >
                        <option v-for="ethnicity in ethnicities" :key="ethnicity.reference" :value="ethnicity.code">{{ ethnicity.name }}</option>
                    </select-input>

                    <h1 class="text-green-600 text-lg w-full">* Gender Identity</h1>
                    <div class="mb-2 w-full border-b-2" />
                    <label class="w-full">Is your current gender identity the same as the gender originally assigned at birth?</label>
                    <div class="w-full text-sm mb-2">(If you do not wish to provide this information then please select 'Information Refused')</div>
                    <select-input v-model="form.gender_identity"
                                  class="pr-6"
                                  :error="form.errors?.gender_identity"
                                  @update:modelValue="value = $event"
                    >
                        <option v-for="item in genderIdentity" :key="item.reference" :value="item.code">{{ item.name }}</option>
                    </select-input>

                    <h1 class="text-green-600 text-lg w-full">* Religious Belief</h1>
                    <div class="mb-2 w-full border-b-2" />
                    <label class="w-full">Please indicate your religious belief</label>
                    <div class="w-full text-sm mb-2">(If you do not wish to provide this information then please select 'Information Refused')</div>
                    <select-input v-model="form.religious_belief"
                                  class="pr-6"
                                  :error="form.errors?.religious_belief"
                                  @update:modelValue="value = $event"
                    >
                        <option v-for="item in religiousBelief" :key="item.reference" :value="item.code">{{ item.name }}</option>
                    </select-input>

                    <h1 class="text-green-600 text-lg w-full">* Sexual Orientation</h1>
                    <div class="mb-2 w-full border-b-2" />
                    <label class="w-full">Please indicate your sexual orientation</label>
                    <div class="w-full text-sm mb-2">(If you do not wish to provide this information then please select 'Information Refused')</div>
                    <select-input v-model="form.sexual_orientation"
                                  class="pr-6"
                                  :error="form.errors?.sexual_orientation"
                                  @update:modelValue="value = $event"
                    >
                        <option v-for="item in sexualOrientation" :key="item.reference" :value="item.code">{{ item.name }}</option>
                    </select-input>

                    <h1 class="text-green-600 text-lg w-full">* Criminal Conviction</h1>
                    <div class="mb-2 w-full border-b-2" />
                    <div class="text-left mb-2 text-sm">
                        The School is committed to assessing all applications on their academic merit. However, it does have to
                        balance this with its duty to ensure the safety and wellbeing of its staff and student community. Having a criminal record does not
                        necessarily prevent an individual from studying at the School. The final decision is very much dependent on the background and nature of
                        the offence(s). As part of the application process students are required to declare any relevant ‘unspent’ criminal conviction(s).
                        Convictions that are "spent" are not considered to be relevant and applicants are not required to disclose these. For more information
                        please see our Admissions Policy for <a href="https://www.lshtm.ac.uk/sites/default/files/PGR_Admissions_Policy.pdf" target="_blank"
                                                                class="text-green-600"
                        >Postgraduate Research</a> or <a
                            href="https://www.lshtm.ac.uk/sites/default/files/pgt_admissions_policy.pdf"
                            target="_blank" class="text-green-600"
                        >Postgraduate
                            Taught</a>
                    </div>
                    <label class="w-full">Do you have any unspent criminal convictions?</label>
                    <select-input v-model="form.criminal_conviction"
                                  class="pr-6 mb-4 form-select"
                                  :error="form.errors?.criminal_conviction"
                                  @update:modelValue="value = $event"
                    >
                        <option value="1">Yes</option>
                        <option value="0">No</option>
                    </select-input>

                    <h1 class="text-green-600 text-lg w-full">* Disabilities</h1>
                    <div class="mb-2 w-full border-b-2" />
                    <div class="text-left mb-2 text-sm">
                        <p>The London School of Hygiene & Tropical Medicine aims to create an environment which enables all students to participate fully in University life. In order to ensure that disabled people compete fairly for study within the School, it would be helpful if you could please answer the following question.</p>
                        <br />
                        <p>All information provided is confidential and details of any disability disclosed will not be shared with the programme selection team.</p>
                    </div>
                    <label class="w-full">Please indicate any disabilities or special needs that may require special arrangements or facilities</label>
                    <div class="w-full text-sm mb-2">(If you do not wish to provide this information then please select 'Information Refused')</div>
                    <select-input v-model="form.disabilities"
                                  class="pr-6"
                                  :error="form.errors?.disabilities"
                                  @update:modelValue="value = $event"
                    >
                        <option v-for="disability in disabilities" :key="disability.reference" :value="disability.code">{{ disability.name }}</option>
                    </select-input>

                    <div v-if="showDisabilityQuestions?.name.length > 0 && showDisabilityQuestions?.name !== 'No known impairment, health condition or learning difference' && showDisabilityQuestions?.name !== 'Prefer not to say' && showDisabilityQuestions?.name !== 'No known disability' && showDisabilityQuestions?.name !== 'Information Refused'"
                         class="p-8 pb-2 flex flex-wrap text-left"
                    >
                        <textarea-input
                            v-model="form.disability_details"
                            class="w-full bg-green-200 padding-2"
                            label="Please provide further information"
                            rows="2"
                            @update:modelValue="value = $event"
                        />
                        <p class="pt-2 pb-4">
                            The information you provide in this form is intended to play a positive role in ensuring you are able to fully
                            participate in your course and university life. This is available in alternative formats, including large print.
                            This can be requested by emailing studentadvice@lshtm.ac.uk.
                        </p>

                        <textarea-input
                            v-model="form.disability_effect"
                            class="w-full-i bg-green-200 padding-2"
                            label="How would you describe your disability? Please briefly explain how your disability or medical condition affects you"
                            rows="2"
                            @update:modelValue="value = $event"
                        />

                        <h1 class="text-green-600 text-lg w-full mt-4"><strong>Facilitation and Access Requirements</strong></h1>
                        <p class="text-green-600 text-lg w-full">Support within programme</p>
                        <textarea-input
                            v-model="form.disability_support_required"
                            class="w-full-i bg-green-200 padding-2 mb-4"
                            label="Please describe any support received in the past and/or required now, e.g. information in large print, signer, extra time in examinations. Please give details of all requirements."
                            rows="2"
                            @update:modelValue="value = $event"
                        />

                        <div class="w-full grid grid-cols-5 py-2">
                            <div class="col-span-2">
                                <label class="">Do you require level access to get into buildings?</label>
                            </div>
                            <div class="">
                                <div class="md:inline-block pr-4">
                                    <input id="disability_level_building_access-yes-option"
                                           v-model="form.disability_level_building_access"
                                           :value="1"
                                           type="radio"
                                           name="disability_level_building_access"
                                           @update:modelValue="value = $event"
                                    />
                                    <label for="disability_level_building_access-yes-option" class="ml-2">Yes</label>
                                </div>
                                <div class="md:inline-block">
                                    <input id="disability_level_building_access-no-option"
                                           v-model="form.disability_level_building_access"
                                           :value="0"
                                           type="radio"
                                           name="disability_level_building_access"
                                           @update:modelValue="value = $event"
                                    />
                                    <label for="disability_level_building_access-no-option" class="ml-2">No</label>
                                </div>
                            </div>
                        </div>
                        <div class="w-full grid grid-cols-5 py-2">
                            <div class="col-span-2">
                                <label class="">Do you require lower level lift buttons?</label>
                            </div>
                            <div class="">
                                <div class="md:inline-block pr-4">
                                    <input id="disability_lower_buttons-yes-option"
                                           v-model="form.disability_lower_buttons"
                                           :value="1"
                                           type="radio"
                                           name="disability_lower_buttons"
                                           @update:modelValue="value = $event"
                                    />
                                    <label for="disability_lower_buttons-yes-option" class="ml-2">Yes</label>
                                </div>
                                <div class="md:inline-block">
                                    <input id="disability_lower_buttons-no-option"
                                           v-model="form.disability_lower_buttons"
                                           :value="0"
                                           type="radio"
                                           name="disability_lower_buttons"
                                           @update:modelValue="value = $event"
                                    />
                                    <label for="disability_lower_buttons-no-option" class="ml-2">No</label>
                                </div>
                            </div>
                        </div>
                        <div class="w-full grid grid-cols-5 py-2">
                            <div class="col-span-2">
                                <label class="">Can you climb stairs?</label>
                            </div>
                            <div class="">
                                <div class="md:inline-block pr-4">
                                    <input id="disability_climb_stairs-yes-option"
                                           v-model="form.disability_climb_stairs"
                                           :value="1"
                                           type="radio"
                                           name="disability_climb_stairs"
                                           @update:modelValue="value = $event"
                                    />
                                    <label for="disability_climb_stairs-yes-option" class="ml-2">Yes</label>
                                </div>
                                <div class="md:inline-block">
                                    <input id="disability_climb_stairs-no-option"
                                           v-model="form.disability_climb_stairs"
                                           :value="0"
                                           type="radio"
                                           name="disability_climb_stairs"
                                           @update:modelValue="value = $event"
                                    />
                                    <label for="disability_climb_stairs-no-option" class="ml-2">No</label>
                                </div>
                            </div>
                        </div>
                        <div class="w-full grid grid-cols-5 py-2">
                            <div class="col-span-2">
                                <label class="">Can you use an adapted lift independently?</label>
                            </div>
                            <div class="">
                                <div class="md:inline-block pr-4">
                                    <input id="disability_adapted_lift-yes-option"
                                           v-model="form.disability_adapted_lift"
                                           :value="1"
                                           type="radio"
                                           name="disability_adapted_lift"
                                           @update:modelValue="value = $event"
                                    />
                                    <label for="disability_adapted_lift-yes-option" class="ml-2">Yes</label>
                                </div>
                                <div class="md:inline-block">
                                    <input id="disability_adapted_lift-no-option"
                                           v-model="form.disability_adapted_lift"
                                           :value="0"
                                           type="radio"
                                           name="disability_adapted_lift"
                                           @update:modelValue="value = $event"
                                    />
                                    <label for="disability_adapted_lift-no-option" class="ml-2">No</label>
                                </div>
                            </div>
                        </div>
                        <div class="w-full grid grid-cols-5 py-2">
                            <div class="col-span-2">
                                <label class="">Can you find your way in unfamiliar buildings?</label>
                            </div>
                            <div class="">
                                <div class="md:inline-block pr-4">
                                    <input id="disability_unfamiliar_buildings-yes-option"
                                           v-model="form.disability_unfamiliar_buildings"
                                           :value="1"
                                           type="radio"
                                           name="disability_unfamiliar_buildings"
                                           @update:modelValue="value = $event"
                                    />
                                    <label for="disability_unfamiliar_buildings-yes-option" class="ml-2">Yes</label>
                                </div>
                                <div class="md:inline-block">
                                    <input id="disability_unfamiliar_buildings-no-option"
                                           v-model="form.disability_unfamiliar_buildings"
                                           :value="0"
                                           type="radio"
                                           name="disability_unfamiliar_buildings"
                                           @update:modelValue="value = $event"
                                    />
                                    <label for="disability_unfamiliar_buildings-no-option" class="ml-2">No</label>
                                </div>
                            </div>
                        </div>
                        <div class="w-full grid grid-cols-5 py-2">
                            <div class="col-span-2">
                                <label class="">Are you able to open weighted doors independently?</label>
                            </div>
                            <div class="">
                                <div class="md:inline-block pr-4">
                                    <input id="disability_weighted_doors-yes-option"
                                           v-model="form.disability_weighted_doors"
                                           :value="1"
                                           type="radio"
                                           name="disability_weighted_doors"
                                           @update:modelValue="value = $event"
                                    />
                                    <label for="disability_weighted_doors-yes-option" class="ml-2">Yes</label>
                                </div>
                                <div class="md:inline-block">
                                    <input id="disability_weighted_doors-no-option"
                                           v-model="form.disability_weighted_doors"
                                           :value="0"
                                           type="radio"
                                           name="disability_weighted_doors"
                                           @update:modelValue="value = $event"
                                    />
                                    <label for="disability_weighted_doors-no-option" class="ml-2">No</label>
                                </div>
                            </div>
                        </div>
                        <div class="pt-4">
                            <p><strong>Safety</strong></p>
                            <p>
                                We need to consider whether in the event of emergency evacuation of a building, you would require provision such as a personalised
                                emergency evacuation procedure for yourself, or other assistance.
                            </p>
                        </div>
                        <div class="w-full grid grid-cols-5 pt-8">
                            <div class="col-span-2">
                                <label class="">Are you able to hear a fire alarm?</label>
                            </div>
                            <div class="">
                                <div class="md:inline-block pr-4">
                                    <input id="disability_hear_alarms-yes-option"
                                           v-model="form.disability_hear_alarms"
                                           :value="1"
                                           type="radio"
                                           name="disability_hear_alarms"
                                           @update:modelValue="value = $event"
                                    />
                                    <label for="disability_hear_alarms-yes-option" class="ml-2">Yes</label>
                                </div>
                                <div class="md:inline-block">
                                    <input id="disability_hear_alarms-no-option"
                                           v-model="form.disability_hear_alarms"
                                           :value="0"
                                           type="radio"
                                           name="disability_hear_alarms"
                                           @update:modelValue="value = $event"
                                    />
                                    <label for="disability_hear_alarms-no-option" class="ml-2">No</label>
                                </div>
                            </div>
                        </div>
                        <div class="w-full grid grid-cols-5 py-2">
                            <div class="col-span-2">
                                <label class="">Are you able to use stairs unaided if the alarm sounds?</label>
                            </div>
                            <div class="">
                                <div class="md:inline-block pr-4">
                                    <input id="disability_use_stairs_unaided-yes-option"
                                           v-model="form.disability_use_stairs_unaided"
                                           :value="1"
                                           type="radio"
                                           name="disability_use_stairs_unaided"
                                           @update:modelValue="value = $event"
                                    />
                                    <label for="disability_use_stairs_unaided-yes-option" class="ml-2">Yes</label>
                                </div>
                                <div class="md:inline-block">
                                    <input id="disability_use_stairs_unaided-no-option"
                                           v-model="form.disability_use_stairs_unaided"
                                           :value="0"
                                           type="radio"
                                           name="disability_use_stairs_unaided"
                                           @update:modelValue="value = $event"
                                    />
                                    <label for="disability_use_stairs_unaided-no-option" class="ml-2">No</label>
                                </div>
                            </div>
                        </div>

                        <div class="pt-4 w-full">
                            <p>If yes, would you find it easier to do so once the main rush of people have gone past?</p>
                            <p>If no, would you be able to exit the building speedily provided you could do this on the same level as the exit?</p>
                        </div>
                        <div class="pt-4 w-full">
                            <textarea-input
                                v-model="form.disability_use_stairs_details"
                                class="w-full-i bg-green-200 padding-2"
                                label="Please provide any further relevant details"
                                rows="2"
                                @update:modelValue="value = $event"
                            />
                        </div>
                        <div class="w-full grid grid-cols-5 py-2">
                            <div class="col-span-2">
                                <label class="">Do you require named people to assist you in leaving a building?</label>
                            </div>
                            <div class="">
                                <div class="md:inline-block pr-4">
                                    <input id="disability_named_people_egress-yes-option"
                                           v-model="form.disability_named_people_egress"
                                           :value="1"
                                           type="radio"
                                           name="disability_named_people_egress"
                                           @update:modelValue="value = $event"
                                    />
                                    <label for="disability_named_people_egress-yes-option" class="ml-2">Yes</label>
                                </div>
                                <div class="md:inline-block">
                                    <input id="disability_named_people_egress-no-option"
                                           v-model="form.disability_named_people_egress"
                                           :value="0"
                                           type="radio"
                                           name="disability_named_people_egress"
                                           @update:modelValue="value = $event"
                                    />
                                    <label for="disability_named_people_egress-no-option" class="ml-2">No</label>
                                </div>
                            </div>
                        </div>
                        <div class="pt-4">
                            <p><strong>Residential Accommodation</strong></p>
                            <p>Please note that LSHTM does not have its own accommodation but may be able to assist you in finding suitable accommodation.</p>
                        </div>
                        <div class="pt-4 w-full">
                            <textarea-input
                                v-model="form.disability_accommodation"
                                class="w-full-i bg-green-200 padding-2"
                                label="If you require adapted accommodation please specify your requirements, e.g. ground floor, wheelchair accessible accommodation, or particular facilities"
                                rows="2"
                                @update:modelValue="value = $event"
                            />
                        </div>
                        <div class="w-full grid grid-cols-5 py-2">
                            <div class="col-span-2">
                                <label class="">Do you need personal care assistance?</label>
                            </div>
                            <div class="">
                                <div class="md:inline-block pr-4">
                                    <input id="disability_personal_care_assistance-yes-option"
                                           v-model="form.disability_personal_care_assistance"
                                           :value="1"
                                           type="radio"
                                           name="disability_personal_care_assistance"
                                           @update:modelValue="value = $event"
                                    />
                                    <label for="disability_personal_care_assistance-yes-option" class="ml-2">Yes</label>
                                </div>
                                <div class="md:inline-block">
                                    <input id="disability_personal_care_assistance-no-option"
                                           v-model="form.disability_personal_care_assistance"
                                           :value="0"
                                           type="radio"
                                           name="disability_personal_care_assistance"
                                           @update:modelValue="value = $event"
                                    />
                                    <label for="disability_personal_care_assistance-no-option" class="ml-2">No</label>
                                </div>
                            </div>
                        </div>
                        <div class="pt-4 w-full">
                            <p><strong>Library</strong></p>
                        </div>
                        <div class="w-full">
                            <textarea-input
                                v-model="form.disability_library"
                                class="w-full-i bg-green-200 padding-2"
                                label="Please indicate any support or facilities, which may be required within the Library"
                                rows="2"
                                @update:modelValue="value = $event"
                            />
                        </div>
                        <div class="pt-4 w-full">
                            <p><strong>Computer facilities</strong></p>
                        </div>
                        <div class="w-full">
                            <textarea-input
                                v-model="form.disability_computer"
                                class="w-full-i bg-green-200 padding-2"
                                label="If you have any particular requirements for adapted computer facilities please indicate"
                                rows="2"
                                @update:modelValue="value = $event"
                            />
                        </div>
                        <div class="pt-4 w-full">
                            <p><strong>Leisure facilities</strong></p>
                        </div>
                        <div class="w-full">
                            <textarea-input
                                v-model="form.disability_leisure"
                                class="w-full-i bg-green-200 padding-2"
                                label="Please specify any leisure facilities which particularly interest you and any access or facilitation which may be required"
                                rows="2"
                                @update:modelValue="value = $event"
                            />
                        </div>
                        <div class="pt-4 w-full">
                            <p><strong>Other requirements</strong></p>
                        </div>
                        <div class="w-full">
                            <textarea-input
                                v-model="form.disability_other"
                                class="w-full-i bg-green-200 padding-2"
                                label="Please describe any other facilitation or access you feel is required"
                                rows="2"
                                @update:modelValue="value = $event"
                            />
                        </div>
                        <div class="pt-4">
                            <p><strong>Visiting the University</strong></p>
                            <p>We recommend that disabled applicants visit LSHTM specifically to look at access and facilities offered. </p>
                        </div>
                        <div class="w-full grid grid-cols-5 py-2">
                            <div class="col-span-2">
                                <label class="">Please indicate whether you think a visit is possible</label>
                            </div>
                            <div class="">
                                <div class="md:inline-block pr-4">
                                    <input id="disability_visit_possible-yes-option"
                                           v-model="form.disability_visit_possible"
                                           :value="1"
                                           type="radio"
                                           name="disability_visit_possible"
                                           @update:modelValue="value = $event"
                                    />
                                    <label for="disability_visit_possible-yes-option" class="ml-2">Yes</label>
                                </div>
                                <div class="md:inline-block">
                                    <input id="disability_visit_possible-no-option"
                                           v-model="form.disability_visit_possible"
                                           :value="0"
                                           type="radio"
                                           name="disability_visit_possible"
                                           @update:modelValue="value = $event"
                                    />
                                    <label for="disability_visit_possible-no-option" class="ml-2">No</label>
                                </div>
                            </div>
                        </div>
                        <!--                        <div class="pt-4">-->
                        <!--                            <p><strong>Consent to share</strong></p>-->
                        <!--                        </div>-->
                        <!--                        <div class="w-full py-8">-->
                        <!--                            <div class="md:inline-block px-8 pb-4">-->
                        <!--                                <label class="mr-2" for="disability_consent_to_share-yes">-->
                        <!--                                    <input v-model="form.disability_consent_to_share"-->
                        <!--                                           :value="1"-->
                        <!--                                           id="disability_consent_to_share-yes"-->
                        <!--                                           type="radio"-->
                        <!--                                           name="disability_consent_to_share-yes"-->
                        <!--                                           class="mr-2"-->
                        <!--                                           @update:modelValue="value = $event"-->
                        <!--                                    />I consent to appropriate and relevant information being disclosed to my department or other University services in order to make the adjustments I require.</label>-->

                        <!--                            </div>-->
                        <!--                            <div class="md:inline-block px-8">-->
                        <!--                                <label class="mr-2" for="disability_consent_to_share-no">-->
                        <!--                                    <input v-model="form.disability_consent_to_share"-->
                        <!--                                           value="0"-->
                        <!--                                           id="disability_consent_to_share-no"-->
                        <!--                                           type="radio"-->
                        <!--                                           name="disability_consent_to_share"-->
                        <!--                                           class="mr-2"-->
                        <!--                                           @update:modelValue="value = $event"-->
                        <!--                                    />I do not consent to my disclosed information being shared (this may prevent or limit support arrangements).</label>-->

                        <!--                            </div>-->
                        <!--                        </div>-->
                        <div>
                            <p>For any queries, please contact the Student Adviser via email; disability@lshtm.ac.uk, or Tel: +44 (0)20 7299 4706</p>
                            <p>All information on this section of your application will be treated confidentially.</p>
                        </div>
                    </div>
                </div>
                <div v-if="successMessage" class="success-message m-8 w-auto">{{ successMessage }}</div>
                <div class="lg:flex justify-end w-full pb-2 -mt-8">
                    <div class="relative w-auto lg:w-1/3 mx-8">
                        <application-component-save-button :disabled="formDirty" class="w-full flex btn-green ml-auto mt-8 md:inline-block text-center"
                                                           type="submit"
                        >
                            Save
                            <div v-if="loading" class="lds-ring-container absolute top-10">
                                <div class="lds-ring">
                                    <div />
                                    <div />
                                    <div />
                                    <div />
                                </div>
                            </div>
                        </application-component-save-button>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import SelectInput from '@/shared/SelectInput'
import ApplicationComponentSaveButton from '@/shared/ApplicationComponentSaveButton'
import TextareaInput from '@/shared/TextareaInput'
import TextInput from '@/shared/TextInput'

export default {
    components: {
        TextInput,
        ApplicationComponentSaveButton,
        SelectInput,
        TextareaInput,
    },
    props: {
        equalOpportunities: Object,
        ethnicities: Object,
        disabilities: Object,
        religiousBelief: Object,
        sexualOrientation: Object,
        genderIdentity: Object,
        sourcesOfInterest: Object,
        applicationReference: String,
    },
    data() {
        return {
            form: ({
                _method: 'PUT',
                reference: this.equalOpportunities.reference,
                sources_of_interest: this.equalOpportunities.sources_of_interest,
                ethnic_origin: this.equalOpportunities.ethnic_origin,
                gender_identity: this.equalOpportunities.gender_identity,
                religious_belief: this.equalOpportunities.religious_belief,
                sexual_orientation: this.equalOpportunities.sexual_orientation,
                criminal_conviction: this.equalOpportunities.criminal_conviction,
                disabilities: this.equalOpportunities.disabilities,
                disability_details: this.equalOpportunities.disability_details,
                disability_effect: this.equalOpportunities.disability_effect,
                disability_support_required: this.equalOpportunities.disability_support_required,
                disability_level_building_access: this.equalOpportunities.disability_level_building_access,
                disability_lower_buttons: this.equalOpportunities.disability_lower_buttons,
                disability_climb_stairs: this.equalOpportunities.disability_climb_stairs,
                disability_adapted_lift: this.equalOpportunities.disability_adapted_lift,
                disability_unfamiliar_buildings: this.equalOpportunities.disability_unfamiliar_buildings,
                disability_weighted_doors: this.equalOpportunities.disability_weighted_doors,
                disability_hear_alarms: this.equalOpportunities.disability_hear_alarms,
                disability_use_stairs_unaided: this.equalOpportunities.disability_use_stairs_unaided,
                disability_use_stairs_details: this.equalOpportunities.disability_use_stairs_details,
                disability_named_people_egress: this.equalOpportunities.disability_named_people_egress,
                disability_accommodation: this.equalOpportunities.disability_accommodation,
                disability_personal_care_assistance: this.equalOpportunities.disability_personal_care_assistance,
                disability_library: this.equalOpportunities.disability_library,
                disability_computer: this.equalOpportunities.disability_computer,
                disability_leisure: this.equalOpportunities.disability_leisure,
                disability_other: this.equalOpportunities.disability_other,
                disability_visit_possible: this.equalOpportunities.disability_visit_possible,
                disability_consent_to_share: this.equalOpportunities.disability_consent_to_share,
                application_id: this.applicationReference,
                errors: {
                },
            }),
            formDirty: false,
            loading: false,
            successMessage: '',
            showSection: '',
        }
    },
    computed: {
        showDisabilityQuestions() {
            return this.disabilities.filter(disability => disability['code'] === this.form.disabilities)[0]
        },
        validationErrors: {
            get: function() {
                return this.form.errors
            },
            set: function(newValue) {
                for (const key in newValue)
                {
                    this.form.errors[key] = newValue[key].join(' ')
                }
            },
        },
    },
    methods: {
        update() {
            this.loading = true

            let method = ''
            if (this.form.reference === null || this.form.reference === undefined)
            {
                method = 'createEqualOpportunities'
                this.form._method = 'POST'
            }
            else
            {
                method = 'updateEqualOpportunities'
            }

            this.$store.dispatch(method, {
                equalOpportunitiesData: this.form,
                applicationReference: this.applicationReference,
            })
            .then((response) => {
                this.loading = false
                this.form.errors = {}
                this.successMessage = response.data.message
                this.form.reference = response.data.reference
                window.location.search += '&section=equalOpportunitiesShow'
            })
            .catch(error => {
                this.loading = false
                this.form.errors = error.response.data.errors
                this.validationErrors = error.response.data.errors
                this.successMessage = ''
            })
            this.formDirty = false
        },
        dirtyForm() {
            this.formDirty = true
            this.successMessage = ''
        },
    },
}
</script>